import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SHARED_LIB_CONFIG, SharedLibConfig } from '../config/lib-config';
import { DynamicStats } from '../models/entity-model-customization.model';
import { Filter, PageInfo } from '../models/dynamic-filter.model';

export enum BuildingStatsOption {
  Total = 0,
  Tagged = 1,
  Retagged = 2,
  Inaccessible = 3,
  TotalMeters = 4,
  InaccessibleNumberOfRevisits = 5,
}

const propertyNameToOption: Record<string, BuildingStatsOption> = {
  Total: BuildingStatsOption.Total,
  Tagged: BuildingStatsOption.Tagged,
  Retagged: BuildingStatsOption.Retagged,
  Inaccessible: BuildingStatsOption.Inaccessible,
  TotalMeters: BuildingStatsOption.TotalMeters,
  InaccessibleRevisited: BuildingStatsOption.InaccessibleNumberOfRevisits,
};

@Injectable({ providedIn: 'root' })
export class BuildingService {
  private pathApi: string;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private http: HttpClient,
    @Inject(SHARED_LIB_CONFIG) private config: SharedLibConfig,
  ) {
    this.pathApi = this.config.pathApi + 'building/';
  }

  getStatsDynamic(obj: DynamicStats): Observable<any> {
    const propertyName = obj.metadataItem?.additionalStatsInfo?.propertyName ?? '';
    const option = propertyNameToOption[propertyName];
    const fp: any = obj.filter?.filterParams ?? {};
    const hasFilter =
      (fp.selectedAreas?.length ?? 0) > 0 ||
      (fp.selectedRegions?.length ?? 0) > 0 ||
      (fp.selectedSubgeographicalRegions?.length ?? 0) > 0 ||
      (fp.selectedUsers?.length ?? 0) > 0 ||
      (fp.validationStatuses?.length ?? 0) > 0 ||
      fp.reviewStatus != null ||
      fp.tag != null ||
      !!fp.dateFrom || !!fp.dateTo ||
      (obj.filter?.dynamicFilters?.length ?? 0) > 0 ||
      !!obj.filter?.searchObject?.searchValue;
    const payload = {
      filter: mapToBuildingsFilter(obj.filter),
      option,
      isGetAll: !hasFilter,
    };
    return this.http
      .post(`${this.pathApi}getStats`, payload, {
        headers: this.headers,
        observe: 'response',
      })
      .pipe(
        map((res: any) => ({
          ...res,
          body: { ...res.body, data: res.body?.data?.count ?? 0 },
        })),
      );
  }

  getAllDynamic(filter: Filter): Observable<any> {
    const dataIn = {
      ...filter,
      filterParams: mapToBuildingsFilter(filter),
    };
    return this.http.post(`${this.pathApi}getAllDynamic`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  downloadDynamic(filter: Filter): Observable<any> {
    const dataIn = {
      ...filter,
      pageInfo: new PageInfo(-1, -1),
      filterParams: mapToBuildingsFilter(filter),
    };
    return this.http.post(`${this.pathApi}downloadFileDynamic`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }
}

function mapToBuildingsFilter(filter: Filter): any {
  const fp: any = filter?.filterParams;
  if (!fp) return {};
  return {
    ...fp,
    selectedAreas: fp.selectedAreas?.length ? fp.selectedAreas : (fp.selectedAreaModels?.map((a: any) => a.id) ?? []),
    selectedUsers: fp.selectedUsers?.length ? fp.selectedUsers : (fp.selectedUserModels?.map((u: any) => u.id) ?? []),
    selectedRegions: fp.selectedRegions?.length ? fp.selectedRegions : (fp.selectedRegionModel ? [fp.selectedRegionModel.id] : (fp.selectedRegion ? [fp.selectedRegion.id] : [])),
    validationStatuses: fp.validationStatuses?.length ? fp.validationStatuses : (fp.statuses ?? []),
  };
}
